import React from "react";
import QRCode from "qrcode";
import { X, Download, Share2, Copy } from "lucide-react";
import { toast } from "sonner";

const fmt = (n, d = 2) => (n === null || n === undefined ? "—" : Number(n).toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d }));
const esc = (s) => String(s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

function buildSvg(d, name, qr, refCode) {
  const profit = Number(d.profit || 0);
  const win = profit >= 0;
  const accent = win ? "#34d399" : "#f43f5e";
  const sign = profit >= 0 ? "+" : "";
  return `<svg xmlns="http://www.w3.org/2000/svg" width="600" height="600" viewBox="0 0 600 600">
  <defs><linearGradient id="bg" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="#06120c"/><stop offset="0.6" stop-color="#0a2a1e"/><stop offset="1" stop-color="#06120c"/></linearGradient></defs>
  <rect width="600" height="600" fill="url(#bg)"/>
  <rect x="16" y="16" width="568" height="568" rx="26" fill="none" stroke="#d9b36c" stroke-opacity="0.55" stroke-width="2"/>
  <text x="48" y="78" fill="#d9b36c" font-family="Arial, sans-serif" font-size="24" font-weight="800" letter-spacing="4">ALEXA FINTECH</text>
  <text x="48" y="104" fill="#8fdcc0" font-family="Arial, sans-serif" font-size="14">Weekly Self-Trader Leaderboard</text>
  <text x="48" y="180" fill="#8fdcc0" font-family="Arial, sans-serif" font-size="22">My Rank</text>
  <text x="48" y="300" fill="#d9b36c" font-family="Arial, sans-serif" font-size="150" font-weight="800">#${d.rank}</text>
  <text x="48" y="380" fill="${accent}" font-family="Arial, sans-serif" font-size="52" font-weight="800">${sign}$${fmt(profit)}</text>
  <text x="48" y="418" fill="#ffffff" font-family="Arial, sans-serif" font-size="22" font-weight="600">profit this week</text>
  <text x="48" y="478" fill="#8fdcc0" font-family="Arial, sans-serif" font-size="18">${d.trades || 0} trades   ·   ${fmt(d.win_rate || 0)}% win rate</text>
  <text x="48" y="556" fill="#d9b36c" font-family="Arial, sans-serif" font-size="18" font-weight="700">${esc(name || "ALEXA Trader")}</text>
  ${qr ? `<rect x="432" y="430" width="128" height="128" rx="12" fill="#ffffff"/><image href="${qr}" x="440" y="438" width="112" height="112"/><text x="496" y="428" fill="#8fdcc0" font-family="Arial, sans-serif" font-size="13" text-anchor="middle">Scan to join</text>${refCode ? `<text x="496" y="576" fill="#5f8f7d" font-family="Arial, sans-serif" font-size="12" text-anchor="middle">ref: ${esc(refCode)}</text>` : ""}` : ""}
</svg>`;
}

async function svgToBlob(svg) {
  const url = "data:image/svg+xml;base64," + btoa(unescape(encodeURIComponent(svg)));
  const img = new Image();
  await new Promise((res, rej) => { img.onload = res; img.onerror = rej; img.src = url; });
  const canvas = document.createElement("canvas");
  canvas.width = 600; canvas.height = 600;
  canvas.getContext("2d").drawImage(img, 0, 0);
  return await new Promise((res) => canvas.toBlob(res, "image/png"));
}

export default function RankShareCard({ data, user, onClose }) {
  const [qr, setQr] = React.useState("");
  const refCode = user?.member_id || "";
  const refUrl = `${typeof window !== "undefined" ? window.location.origin : ""}/register?sponsor=${refCode}`;
  React.useEffect(() => {
    if (!data) return;
    QRCode.toDataURL(refUrl, { margin: 1, width: 224, color: { dark: "#06120c", light: "#ffffff" } }).then(setQr).catch(() => setQr(""));
  }, [data, refUrl]);
  if (!data) return null;
  const name = user?.name || refCode || "ALEXA Trader";
  const svg = buildSvg(data, name, qr, refCode);
  const preview = "data:image/svg+xml;base64," + btoa(unescape(encodeURIComponent(svg)));
  const profit = Number(data.profit || 0);
  const text = `I'm ranked #${data.rank} this week on the ALEXA FINTECH self-trading leaderboard with ${profit >= 0 ? "+" : ""}$${fmt(profit)} profit! Join my team: ${refUrl}`;

  const download = async () => {
    try { const blob = await svgToBlob(svg); const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = `alexa-rank-${data.rank}.png`; a.click(); URL.revokeObjectURL(a.href); }
    catch { toast.error("Could not generate image"); }
  };
  const copy = async () => { try { await navigator.clipboard.writeText(text); toast.success("Caption copied"); } catch { toast.error("Copy failed"); } };
  const share = async () => {
    try {
      const blob = await svgToBlob(svg);
      const file = new File([blob], "alexa-rank.png", { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) { await navigator.share({ files: [file], text }); }
      else if (navigator.share) { await navigator.share({ text }); }
      else { await copy(); }
    } catch { /* cancelled */ }
  };

  return (
    <div className="fixed inset-0 z-[70] grid place-items-center overflow-y-auto bg-black/80 p-4" data-testid="rank-share-card">
      <div className="w-full max-w-sm rounded-2xl p-4" style={{ background: "linear-gradient(160deg,#0f1613,#0a0f0c)", border: "1px solid rgba(217,179,108,.4)" }}>
        <div className="mb-3 flex items-center justify-between">
          <h3 className="flex items-center gap-2 text-base font-extrabold text-white"><Share2 size={16} className="text-amber-400" /> Share My Rank</h3>
          <button onClick={onClose} className="text-white/60 hover:text-white" data-testid="rank-share-close"><X size={18} /></button>
        </div>
        <img src={preview} alt="Rank card" className="mb-3 w-full rounded-xl border border-white/10" />
        <div className="grid grid-cols-3 gap-2">
          <button onClick={download} data-testid="rank-share-download" className="flex items-center justify-center gap-1 rounded-lg bg-amber-400 py-2.5 text-xs font-extrabold text-black hover:bg-amber-300"><Download size={14} /> PNG</button>
          <button onClick={copy} data-testid="rank-share-copy" className="flex items-center justify-center gap-1 rounded-lg border border-amber-400/40 py-2.5 text-xs font-bold text-amber-300 hover:bg-amber-400/10"><Copy size={14} /> Caption</button>
          <button onClick={share} data-testid="rank-share-native" className="flex items-center justify-center gap-1 rounded-lg border border-emerald-500/40 py-2.5 text-xs font-bold text-emerald-300 hover:bg-emerald-500/10"><Share2 size={14} /> Share</button>
        </div>
        <div className="mt-2 flex items-center gap-2 rounded-lg bg-black/30 p-2">
          <input readOnly value={refUrl} className="w-full truncate bg-transparent text-[10px] text-emerald-200/70 outline-none" data-testid="rank-ref-link" />
          <button onClick={() => { navigator.clipboard.writeText(refUrl); toast.success("Referral link copied"); }} data-testid="rank-copy-link" className="rounded bg-amber-400/20 px-2 py-1 text-[10px] font-bold text-amber-300">Copy Link</button>
        </div>
      </div>
    </div>
  );
}
